import type { MapDispatchToProps } from 'react-redux';
import { connect } from 'react-redux';
import { TabIdProps } from 'shared/props';
import { isDynamicFormLoaded } from 'state/dynamic/selectors';
import type { ProductModificationStatus } from 'state/productModificationForm';
import {
  productModificationFightingBackCommentChanged,
  productModificationFightingBackJustificationChanged,
  productModificationPublicOfferingDisclaimerConfirmationChanged,
} from 'state/productModificationForm/productModificationForm.action';
import * as ProductModificationSelectors from 'state/productModificationForm/productModificationForm.selector';
import {
  productModificationApplyRequestedThunk,
  productModificationReloadRequestedThunk,
} from 'state/productModificationForm/productModificationForm.thunks';
import { isPendingEnrichment } from 'state/selectors';
import { getDynamicFormInfos, getFormInfos } from 'state/selectors/common';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { isProductModificationApplicableForUser } from 'state/user';

interface StateProps {
  fightingBackJustification?: string;
  fightingBackComment?: string;

  hasConfirmedPublicOfferingDisclaimer: boolean;
  hasValidationErrors: boolean;
  showPublicOfferingDisclaimer: boolean;
  status: ProductModificationStatus;
  isReady: boolean;
  requestId?: string;
  showReloadInformation: boolean;
  canApply: boolean;
  isLoading: boolean;
}

interface DispatchProps {
  fightingBackJustificationChanged: (newJustification: string) => void;
  fightingBackCommentChanged: (newComment: string) => void;
  hasConfirmedPublicOfferingDisclaimerChanged: (newComment: boolean) => void;

  applyProductModification: () => void;
  reloadProductModification: () => void;
}
export type SidePanelProps = TabIdProps & StateProps & DispatchProps;

const mapStateToProps = (state: AppState, { tabId }: TabIdProps): StateProps => {
  const hasApplyRights = isProductModificationApplicableForUser(state);

  const requestId = ProductModificationSelectors.getRequestId(state, tabId);
  const fightingBackComment = ProductModificationSelectors.getFightingBackComment(state, tabId);
  const fightingBackJustification = ProductModificationSelectors.getFightingBackJustification(state, tabId);
  const status = ProductModificationSelectors.getStatus(state, tabId);
  const showPublicOfferingDisclaimer = ProductModificationSelectors.getShowPublicOfferingDisclaimer(state, tabId);
  const infos = getDynamicFormInfos(state, tabId);
  const isEnrichmentDone = !infos?.enrichmentError && !isPendingEnrichment(state, tabId);
  const isReady = ProductModificationSelectors.isReady(state, tabId) && isEnrichmentDone;

  const showReloadInformation = ProductModificationSelectors.getShowReloadInformation(state, tabId);
  const hasConfirmedPublicOfferingDisclaimer = ProductModificationSelectors.hasConfirmedPublicOfferingDisclaimer(
    state,
    tabId,
  );

  const formInfos = getFormInfos(state, 'dynamic', tabId);
  const hasValidationErrors = !!formInfos?.validationErrors;
  const isLoading = !isDynamicFormLoaded(state, tabId);
  return {
    showReloadInformation,
    requestId,
    isReady,
    fightingBackComment,
    fightingBackJustification,
    status,
    showPublicOfferingDisclaimer,
    hasConfirmedPublicOfferingDisclaimer,
    hasValidationErrors,
    canApply: hasApplyRights,
    isLoading,
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, TabIdProps> = (dispatch: ThunkDispatch, { tabId }) => ({
  fightingBackJustificationChanged: fightingBackJustification => {
    dispatch(productModificationFightingBackJustificationChanged(tabId, fightingBackJustification));
  },
  fightingBackCommentChanged: fightingBackComment => {
    dispatch(productModificationFightingBackCommentChanged(tabId, fightingBackComment));
  },
  hasConfirmedPublicOfferingDisclaimerChanged: hasConfirmed => {
    dispatch(productModificationPublicOfferingDisclaimerConfirmationChanged(tabId, hasConfirmed));
  },

  applyProductModification: () => {
    dispatch(productModificationApplyRequestedThunk(tabId, window.sgmeConfiguration.productModificationTimeoutMs));
  },
  reloadProductModification: () => {
    dispatch(productModificationReloadRequestedThunk(tabId));
  },
});

export const connectSidePanel = connect(mapStateToProps, mapDispatchToProps);
